import * as DocumentPicker from 'expo-document-picker';
import { View, Text, Pressable, Platform, Linking } from "react-native";
import { choosePhoto, formatSize, getFile } from '@/utils/FileManager';
import FontAwesome5 from "@react-native-vector-icons/fontawesome5";
import { useUnistyles } from 'react-native-unistyles';
import { memo } from 'react';
import FileViewer from "react-native-file-viewer";
import usePlatform from '@/utils/hooks/usePlatform';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '../Unicon';

const FileInput = ({title, assets, multiple = false, showPhotoLibrary = false, onSelect, onRemoveAsset, isNotOnSurface = false, acceptedTypes = '*/*', disabled = false, testID}) => {
    const {theme: {colors}} = useUnistyles();
    const { isNative } = usePlatform();

    const FileItem = memo(({file, index = null}) => {
        const isUrl = typeof file === 'string';
        const fileObj = isUrl ? null : file;

        return <Pressable style={styles.fileItem} onPress={() => {
            if(isUrl){
                // Open URL in browser
                if(Platform.OS === 'web'){
                    window.open(file, '_blank');
                } else {
                    Linking.openURL(file);
                }
            }
            else if(fileObj?.key){
                const downloadedFile = getFile(fileObj?.key);
                if(downloadedFile?.uri){
                    if(Platform.OS !== 'web' && FileViewer){
                        FileViewer.open(downloadedFile?.uri);
                    } else if (Platform.OS === 'web') {
                        window.open(downloadedFile?.uri, '_blank');
                    }
                }
            }
            else if(fileObj?.uri){
                if(Platform.OS !== 'web' && FileViewer){
                    FileViewer.open(fileObj?.uri);
                } else if (Platform.OS === 'web') {
                    window.open(fileObj?.uri, '_blank');
                }
            }
        }}>
            <View style={styles.fileInfo}>
                <Text style={styles.fileName}>{isUrl ? file : (fileObj?.name || fileObj?.filename)}</Text>
            </View>
            <View style={styles.fileActions}>
                {!isUrl && <Text style={styles.fileSize}>{formatSize(fileObj?.size)}</Text>}
                <Pressable disabled={disabled} onPress={(e) => {
                    e.stopPropagation();
                    onRemoveAsset(isUrl ? file : fileObj?.uri, index);
                }} style={styles.closeButton}>
                    <Unicon name="close" size={22} colorVariant="textSecondary" />
                </Pressable>
            </View>
        </Pressable>
    })

    return (
        <View style={styles.container}>
            {title && <View style={styles.labelContainer}>
                <Text style={styles.labelText}>{title}</Text>
            </View>}
            <Pressable testID={testID} disabled={disabled} onPress={ async () => {
                const picker = await DocumentPicker.getDocumentAsync({ multiple: multiple, type: acceptedTypes });

                if(!picker.canceled && picker.assets){
                    onSelect(picker.assets)
                }

            }} style={styles.dropZone(isNotOnSurface)}>
                <View style={styles.dropZoneContent}>
                    <View style={styles.dropZoneTextContainer}>
                        <Text style={styles.dropZoneText}>{multiple ? "Select files to upload" : "Select a file to upload"}</Text>
                    </View>
                    <View style={styles.iconGroup}>
                        <View style={styles.iconWrapper}><FontAwesome5 name="file-alt" color={colors.textSecondary} size={25} iconStyle="solid" /></View>
                        
                        {(showPhotoLibrary && isNative) &&
                            <Pressable disabled={disabled} onPress={async () => {
                                const photos = await choosePhoto({multiple})
                                onSelect(photos)
                            }}>
                                <View style={styles.iconWrapper}><FontAwesome5 name="images" color={colors.textSecondary} size={28} iconStyle="solid" /></View>
                            </Pressable>
                        }
                    </View>
                </View>

                        
                {(Array.isArray(assets) && assets?.filter(a => a).length > 0)
                    ? <View style={styles.fileList}>
                        {assets.map((file, index) => {
                            return <FileItem key={index} file={file} index={index} />
                        })}
                    </View>
                    : <View style={styles.singleFileContainer(assets)}>
                        {assets && <FileItem file={assets} index={0} /> }
                    </View>
                }
            </Pressable>
        </View>
    );
}

const styles = StyleSheet.create(({colors, fonts, shadows, spacing, roundness, typography}, rt) => ({
    labelContainer: {
        paddingBottom: 6,
    },
    labelText: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 13,
    },
    container: {
        marginVertical: spacing.sm,
    },
    fileItem: {
        backgroundColor: colors.surface,
        borderRadius: roundness.md,
        marginVertical: 4,
        padding: 12,
        flexDirection: 'row',
        alignItems: "center",
        justifyContent: "space-between",
        _web: {
            transition: "background-color 0.15s ease",
            _hover: {
                backgroundColor: colors.hoverCard,
            },
        },
    },
    fileInfo: {
        flex: 2,
    },
    fileName: {
        color: colors.text, 
        fontFamily: fonts.bold,
        fontSize: typography.body,
    },
    fileActions: {
        flexDirection: 'row', 
        alignItems: "center", 
        justifyContent: "flex-end", 
        flex: 1,
    },
    fileSize: {
        color: colors.textSecondary, 
        fontStyle: "italic", 
        paddingRight: 10,
        fontSize: typography.caption,
    },
    dropZone: (isNotOnSurface) => ({
        minHeight: Math.max(64, Math.ceil(64 * rt.fontScale)),
        borderRadius: roundness.sm,
        backgroundColor: isNotOnSurface ? colors.surface : colors.inputBackground,
        boxShadow: isNotOnSurface ? shadows.card : shadows.none,
        padding: 20,
        outlineStyle: 'none',
        _web: {
            transition: "all 0.15s ease",
            _focus: {
                boxShadow: shadows.input,
            },
            _hover: {
                backgroundColor: colors.hover,
            }
        }
    }),
    dropZoneContent: {
        flexDirection: "row", 
        alignItems: "center", 
        justifyContent: "space-between",
    },
    dropZoneTextContainer: {
        marginHorizontal: 8,
    },
    dropZoneText: {
        fontSize: typography.body, 
        fontFamily: fonts.heading, 
        color: colors.textSecondary,
    },
    iconGroup: {
        flexDirection: "row", 
        justifyContent: "flex-end",
    },
    iconWrapper: {
        alignItems: "center", 
        justifyContent: "center", 
        marginHorizontal: 8,
    },
    fileList: {
        marginTop: 6,
    },
    singleFileContainer: (assets) => ({
        marginTop: assets ? 6 : 0,
    }),
    closeButton: {
        padding: 4,
    }
}))

export default FileInput